import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Grid } from '@mui/material';
import { formatDate } from 'utils/string';
import PublicService from 'services/PublicService';
import INews from 'interfaces/INews';
import ImageCard from 'components/ImageCard';
import MainTitle from 'components/MainTitle';
import { ImageViewer, ImageViewerItem } from 'components/ImageViewer';
import { Author, Banner, Container, Date, SubTitle, Text, Title, Video } from './styles';
import { useLoading } from 'hooks/useLoading';
import { parseCropHash, getFramedImageStyles } from 'utils/cropUtils';

const Detail = (): JSX.Element => {
  const { slug } = useParams<{ slug: string }>();
  const [news, setNews] = useState<INews | undefined>();
  const { openLoading, closeLoading } = useLoading();

  const service = new PublicService();

  useEffect(() => {
    getNews();
  }, []);

  const getNews = async () => {
    try {
      openLoading();
      const news = await service.getNews(slug);
      setNews(news);
      closeLoading();
    } catch (error) {
      closeLoading();
    }
  };

  const isVideo = !!news?.banner && news.banner.endsWith('.mp4');
  const { cleanUrl: bannerUrl, crop: bannerCrop } = parseCropHash(isVideo ? undefined : news?.banner);
  const { containerStyle: bannerContainerStyle, imageStyle: bannerImageStyle } = getFramedImageStyles(bannerCrop);

  return (
    <Container>
      <MainTitle>Notícias</MainTitle>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container rowSpacing={2}>
          <Grid item xs={12}>
            <Title>{news?.title}</Title>
          </Grid>
          {news?.subtitle && (
            <Grid item xs={12}>
              <SubTitle>{news?.subtitle}</SubTitle>
            </Grid>
          )}
          <Grid item xs={12}>
            {isVideo ? (
              <Video src={news?.banner} controls />
            ) : (
              <Banner style={{ position: 'relative', overflow: 'hidden' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: '-10%',
                    left: '-10%',
                    width: '120%',
                    height: '120%',
                    background: `url(${bannerUrl}) center center / cover no-repeat`,
                    filter: 'blur(15px)',
                    zIndex: 1,
                  }}
                />
                <div style={bannerContainerStyle}>
                  <img src={bannerUrl} alt={news?.title ?? ''} style={bannerImageStyle} />
                </div>
              </Banner>
            )}
          </Grid>
        </Grid>
        <Grid container className="bottom-grids" rowSpacing={2}>
          <Grid item xs={12}>
            <Date>{formatDate(news?.date ?? '')}</Date>
          </Grid>
          <Grid item xs={12}>
            <Author>{news?.author}</Author>
          </Grid>
          <Grid item xs={12}>
            <Text dangerouslySetInnerHTML={{ __html: news?.description ?? '' }} />
          </Grid>
          {news?.link && (
            <Grid item xs={12}>
              <a
                href={news.link.startsWith('http') ? news.link : `https://${news.link}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: 500 }}>
                {news.link}
              </a>
            </Grid>
          )}
        </Grid>
        <Grid container className="bottom-grids" spacing={4}>
          <ImageViewer>
            {news?.images &&
              news.images.map(({ image }, index) => (
                <Grid item key={index} xs={12} sm={6} lg={3}>
                  <ImageViewerItem src={image}>
                    <ImageCard src={image} alt="" />
                  </ImageViewerItem>
                </Grid>
              ))}
          </ImageViewer>
        </Grid>
      </Box>
    </Container>
  );
};

export default Detail;
